import { useState } from "react";
import { Key, Copy, Trash2, Plus, Eye, EyeOff, Globe, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useWorkspaceApiKeys, useCreateApiKey, useRevokeApiKey } from "@/hooks/use-workspace-api-keys";
import { useCurrentUserRole } from "@/hooks/use-workspace-members";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { useToast } from "@/hooks/use-toast";
import { apiKeyNameError } from "@/utils/workspace-rules";

export function ApiKeysTab() {
  const { currentWorkspaceId } = useWorkspace();
  const { data: workspaces } = useWorkspaces();
  const { data: apiKeys } = useWorkspaceApiKeys(currentWorkspaceId);
  const { data: currentRole } = useCurrentUserRole(currentWorkspaceId);
  const createApiKey = useCreateApiKey();
  const revokeApiKey = useRevokeApiKey();
  const { toast } = useToast();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [keyType, setKeyType] = useState<'private' | 'public'>('private');
  const [generatedKey, setGeneratedKey] = useState("");
  const [generatedKeyType, setGeneratedKeyType] = useState<'private' | 'public'>('private');
  const [showGeneratedKey, setShowGeneratedKey] = useState(false);

  const currentWorkspace = workspaces?.find(w => w.id === currentWorkspaceId);
  const canManageKeys = currentRole === "owner" || currentRole === "admin";
  const nameError = apiKeyNameError(newKeyName);
  // One key per press: a second create in flight would make a live key whose secret is never shown.
  const canCreate = !createApiKey.isPending && !!newKeyName.trim() && !nameError;

  // `mutate`, not `mutateAsync`: a refusal is the hook's toast, not an uncaught rejection, and
  // the dialog stays open with the name in it so it can be corrected.
  const handleCreateKey = () => {
    // Enter in the name field reaches here even while the button is off.
    if (!canCreate) return;

    createApiKey.mutate(
      { workspaceId: currentWorkspaceId!, name: newKeyName, keyType },
      {
        onSuccess: (result) => {
          setGeneratedKey(result.api_key);
          setGeneratedKeyType(keyType);
          setNewKeyName("");
          setKeyType('private');
          setShowCreateDialog(false);
          setShowKeyDialog(true);
        },
      },
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "API key copied to clipboard",
    });
  };

  const handleCloseKeyDialog = () => {
    setShowKeyDialog(false);
    setGeneratedKey("");
    setShowGeneratedKey(false);
  };

  if (!canManageKeys) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>You don't have permission to manage API keys</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>API Keys</CardTitle>
              <CardDescription>
                Manage API keys for {currentWorkspace?.name}
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create API Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {apiKeys && apiKeys.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Key Prefix</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      {key.key_type === 'public' ? (
                        <Badge variant="outline" className="gap-1">
                          <Globe className="w-3 h-3" />
                          Public
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <Lock className="w-3 h-3" />
                          Private
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded">{key.key_prefix}...</code>
                    </TableCell>
                    <TableCell>
                      {key.is_active ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>{new Date(key.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {key.last_used_at
                        ? new Date(key.last_used_at).toLocaleDateString()
                        : "Never"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Revoke ${key.name}`}
                        // One revoke at a time: a second press would find the key gone (404).
                        disabled={revokeApiKey.isPending}
                        onClick={() =>
                          revokeApiKey.mutate({
                            keyId: key.id,
                            workspaceId: currentWorkspaceId!,
                          })
                        }
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Key className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No API keys yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first API key to start using the Verbeo AI API
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create API Key
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="[&_[aria-invalid=true]]:border-destructive">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for {currentWorkspace?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="key-name">Key Name</Label>
              <Input
                id="key-name"
                placeholder="Production API Key"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateKey();
                }}
                aria-invalid={Boolean(nameError)}
                aria-describedby={nameError ? "key-name-error" : undefined}
              />
              {nameError && (
                <p id="key-name-error" className="text-sm text-destructive">
                  {nameError}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                Choose a descriptive name to help you identify this key later
              </p>
            </div>

            <div className="space-y-3">
              <Label>Key Type</Label>
              <RadioGroup value={keyType} onValueChange={(v) => setKeyType(v as 'private' | 'public')}>
                <div className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setKeyType('private')}>
                  <RadioGroupItem value="private" id="private" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      <Label htmlFor="private" className="font-medium cursor-pointer">Private API Key</Label>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      For server-side use only. Keep this key secret and never expose it in client-side code.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setKeyType('public')}>
                  <RadioGroupItem value="public" id="public" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      <Label htmlFor="public" className="font-medium cursor-pointer">Public API Key</Label>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Safe to use in client-side code like web widgets. Limited permissions for security.
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateKey} disabled={!canCreate}>
              {createApiKey.isPending ? "Creating..." : "Create Key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show Generated Key Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={handleCloseKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {generatedKeyType === 'public' ? 'Public' : 'Private'} API Key Created
            </DialogTitle>
            <DialogDescription>
              Save this key now - you won't be able to see it again!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Your API Key</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <Input
                    value={generatedKey}
                    readOnly
                    type={showGeneratedKey ? "text" : "password"}
                    className="font-mono text-sm pr-10"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0"
                    aria-label={showGeneratedKey ? "Hide the key" : "Show the key"}
                    onClick={() => setShowGeneratedKey(!showGeneratedKey)}
                  >
                    {showGeneratedKey ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Copy the key"
                  onClick={() => copyToClipboard(generatedKey)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              {generatedKeyType === 'public' ? (
                <p className="text-sm text-muted-foreground">
                  This public key can be safely used in client-side code like web widgets.
                </p>
              ) : (
                <p className="text-sm text-destructive">
                  Keep this private key secret! Never expose it in client-side code.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCloseKeyDialog}>I've Saved My Key</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}